/**
 * Cart Integration — joe-main ↔ Horizon cart
 * Bridges existing cart buttons (ATC, Quick Buy, Quick Add, etc.) to Horizon's cart UI.
 *
 * Responsibilities:
 * 1. Listen for .ajax-submit clicks and POST to /cart/add.js
 * 2. After successful add, fetch updated cart section via Section Rendering API
 * 3. Dispatch Horizon's CartAddEvent so cart-items-component, cart-icon morph themselves
 * 4. Open the cart drawer (document.querySelector('cart-drawer-component').open())
 * 5. Update legacy badge selector [data-bind="itemCount"] for backward compat
 * 6. Intercept [data-ajax-cart-trigger] clicks to open Horizon drawer
 *
 * Exposes: window.cartIntegration.refreshCart(sourceId)  — other callers (QB, utilities, EPA) use this.
 */
(function () {
  'use strict';

  var DEBUG = !!window.HORIZON_DEBUG;

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[HorizonCart]');
    console.log.apply(console, args);
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) {
      log('openDrawer: no cart-drawer-component in DOM (likely on /cart page)');
      return;
    }
    if (typeof drawer.open === 'function') {
      drawer.open();
      log('openDrawer: opened');
    } else {
      log('openDrawer: drawer.open() not a function — bundle may not be loaded');
    }
  }

  function closeMobileMenu() {
    // joe-main's mobile menu scroll-locks body — close it if open so drawer can take over.
    if (document.body.classList.contains('mobile-menu--opened')) {
      log('closeMobileMenu: closing mobile menu before opening drawer');
      var $menu = typeof jQuery !== 'undefined' ? jQuery('.mobile-menu') : null;
      if ($menu && $menu.length) {
        $menu.removeClass('is-visible');
      }
      document.body.classList.remove('mobile-menu--opened');
    }
  }

  function getDrawerSectionId() {
    var el = document.querySelector('cart-drawer-component cart-items-component[data-section-id]');
    return el ? el.dataset.sectionId : null;
  }

  function getPageCartSectionId() {
    var el = document.querySelector('.cart-items-component[data-section-id]:not([data-drawer])');
    return el ? el.dataset.sectionId : null;
  }

  /**
   * Fetch updated cart section HTML, dispatch CartAddEvent so Horizon components morph themselves.
   * @param {string} sourceId - identifier of the caller (e.g. 'atc', 'qb', 'epa')
   */
  function refreshCart(sourceId) {
    var sectionId = getDrawerSectionId() || getPageCartSectionId();
    if (!sectionId) {
      log('refreshCart: no cart section in DOM, cannot refresh');
      return Promise.resolve();
    }

    var renderer = window.HorizonCart && window.HorizonCart.sectionRenderer;
    var CartAddEvent = window.HorizonCart && window.HorizonCart.CartAddEvent;

    if (!renderer || !CartAddEvent) {
      log('refreshCart: HorizonCart namespace not ready');
      return Promise.resolve();
    }

    log('refreshCart: fetching section ' + sectionId + ' (source: ' + sourceId + ')');

    // Build URL pointing at /cart route so section renders with cart context.
    var url = new URL('/cart', window.location.origin);
    return renderer
      .renderSection(sectionId, { url: url })
      .then(function (result) {
        log('refreshCart: section rendered, dispatching CartAddEvent');
        // Build sections payload for the event.
        var sections = {};
        if (result && result.html) {
          sections[sectionId] = result.html;
        }
        document.dispatchEvent(
          new CartAddEvent({}, sourceId || 'cart-integration', {
            source: sourceId || 'cart-integration',
            sections: sections,
          })
        );

        // Update legacy badge selector for backward compat. Fetch item count from /cart.js.
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            updateLegacyBadge(cart.item_count);
          })
          .catch(function (e) {
            log('refreshCart: could not fetch /cart.js for badge update', e);
          });
      })
      .catch(function (e) {
        log('refreshCart: section render failed', e);
      });
  }

  function updateLegacyBadge(count) {
    var badges = document.querySelectorAll('[data-bind="itemCount"]');
    badges.forEach(function (el) { el.textContent = String(count); });
    var triggers = document.querySelectorAll('[data-ajax-cart-trigger]');
    triggers.forEach(function (el) {
      if (count > 0) {
        el.classList.add('has-cart-count');
      } else {
        el.classList.remove('has-cart-count');
      }
    });
  }

  /* ==========================================================================
   * ATC form submit handler
   * ========================================================================== */

  function handleATCSubmit(e) {
    var btn = e.target.closest ? e.target.closest('.ajax-submit') : null;
    if (!btn) return;

    e.preventDefault();
    var form = btn.closest('form');
    if (!form) {
      log('ATC: no enclosing form found');
      return;
    }

    // Variant selection validation (mirrors old z__jsAjaxCart behavior).
    if (btn.hasAttribute('data-options-unselected')) {
      log('ATC: variant not selected, showing warning');
      showFormWarning(
        form,
        (window.Shopify && Shopify.translation && Shopify.translation.select_variant) || 'Please select a variant.'
      );
      return;
    }

    log('ATC: submitting form, data-options-unselected not set');
    clearFormWarning(form);

    // Animate button state
    setButtonSubmitting(btn, true);

    var fd = new FormData(form);
    // Convert to URL-encoded for /cart/add.js (it accepts both, but URL-encoded matches old behavior exactly).
    var body = new URLSearchParams(fd).toString();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: body,
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw err;
          });
        }
        return response.json();
      })
      .then(function (item) {
        log('ATC: added', item);
        setButtonSubmitting(btn, false, 'success');
        return refreshCart('atc').then(function () {
          openDrawer();
        });
      })
      .catch(function (err) {
        log('ATC: error', err);
        setButtonSubmitting(btn, false);
        var msg = (err && (err.message || err.description)) || 'Add to cart failed.';
        showFormWarning(form, msg);
      });
  }

  function setButtonSubmitting(btn, submitting, successFlag) {
    if (submitting) {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('disabled');
    } else {
      btn.removeAttribute('disabled');
      btn.classList.remove('disabled');
      if (successFlag === 'success') {
        var check = btn.querySelector('.checkmark');
        if (check) {
          check.classList.add('checkmark-active');
          setTimeout(function () {
            check.classList.remove('checkmark-active');
          }, 1200);
        }
      }
    }
  }

  function showFormWarning(form, message) {
    var warningEl = form.querySelector('.cart-warning');
    if (!warningEl) return;
    warningEl.innerHTML = '<p class="cart-warning__message animated bounceIn">' + message + '</p>';
    form.classList.add('shopify-product-form--unselected-error');
  }

  function clearFormWarning(form) {
    var warningEl = form.querySelector('.cart-warning');
    if (warningEl) warningEl.innerHTML = '';
    form.classList.remove('shopify-product-form--unselected-error');
  }

  /* ==========================================================================
   * Cart icon → open drawer
   * ========================================================================== */

  function handleCartIconClick(e) {
    var trigger = e.target.closest ? e.target.closest('[data-ajax-cart-trigger]') : null;
    if (!trigger) return;

    // If we are on the /cart page, let the <a href="/cart"> default behavior happen.
    if (!document.querySelector('cart-drawer-component')) {
      log('cart icon click: no drawer in DOM (likely /cart page), letting default nav proceed');
      return;
    }

    // Otherwise, prevent navigation and open drawer.
    e.preventDefault();
    closeMobileMenu();
    openDrawer();
  }

  /* ==========================================================================
   * Diagnostics (Phase 1 tests)
   * ========================================================================== */

  function runDiagnostics() {
    if (!DEBUG) return;

    var results = [];
    function pass(t) { results.push('  [PASS] ' + t); }
    function fail(t) { results.push('  [FAIL] ' + t); }

    // Theme shim
    if (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) {
      pass('Theme.routes.cart_add_url = ' + Theme.routes.cart_add_url);
    } else {
      fail('Theme.routes.cart_add_url missing');
    }
    if (window.Theme && window.Theme.routes && window.Theme.routes.cart_change_url) {
      pass('Theme.routes.cart_change_url = ' + Theme.routes.cart_change_url);
    } else {
      fail('Theme.routes.cart_change_url missing');
    }
    if (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) {
      pass('Theme.routes.cart_update_url = ' + Theme.routes.cart_update_url);
    } else {
      fail('Theme.routes.cart_update_url missing');
    }

    // Custom elements
    var expected = [
      'cart-drawer-component', 'cart-items-component', 'cart-icon',
      'cart-quantity-selector-component', 'cart-discount-component',
      'cart-note', 'scroll-hint', 'accordion-custom', 'text-component',
      'quantity-selector-component',
    ];
    expected.forEach(function (name) {
      if (customElements.get(name)) pass('CE: ' + name);
      else fail('CE: ' + name + ' NOT registered');
    });

    // HorizonCart namespace
    if (window.HorizonCart && window.HorizonCart.sectionRenderer) {
      pass('HorizonCart.sectionRenderer exposed');
    } else {
      fail('HorizonCart.sectionRenderer NOT exposed');
    }
    if (window.HorizonCart && window.HorizonCart.CartAddEvent) {
      pass('HorizonCart.CartAddEvent exposed');
    } else {
      fail('HorizonCart.CartAddEvent NOT exposed');
    }

    // CSS variables
    var root = getComputedStyle(document.documentElement);
    ['--padding-xl', '--sidebar-width', '--font-size--sm', '--animation-speed', '--style-border-width'].forEach(function (v) {
      if (root.getPropertyValue(v).trim()) pass('CSS: ' + v + ' = ' + root.getPropertyValue(v).trim());
      else fail('CSS: ' + v + ' missing');
    });

    // Color scheme
    if (document.querySelector('[class*="color-scheme-"]')) pass('Color scheme class found');
    else fail('No color scheme class found');

    // jQuery coexistence
    if (typeof jQuery !== 'undefined') pass('jQuery ' + jQuery.fn.jquery + ' present');
    else fail('jQuery missing (may break joe-main pages)');

    console.log('[HorizonCart] === Phase 1 Diagnostics ===');
    console.log(results.join('\n'));

    // Section rendering API check
    var sectionId = getDrawerSectionId() || getPageCartSectionId();
    if (sectionId) {
      fetch('/cart?section_id=' + encodeURIComponent(sectionId))
        .then(function (r) {
          console.log('[HorizonCart]   [' + (r.ok ? 'PASS' : 'FAIL') + '] Section render: HTTP ' + r.status + ' for section_id=' + sectionId);
        })
        .catch(function (e) {
          console.log('[HorizonCart]   [FAIL] Section render: ' + e);
        });
    } else {
      console.log('[HorizonCart]   [FAIL] No cart section found in DOM to test section rendering');
    }
  }

  /* ==========================================================================
   * Init
   * ========================================================================== */

  /**
   * Listen for Horizon's cart:update event (dispatched by component-cart-items after
   * quantity changes, discount applies, etc.) and sync the legacy badge selector.
   * The wrapped <cart-icon> doesn't have Horizon's ref="" attributes, so its own
   * event handler is a no-op — we handle badge updates here instead.
   */
  function handleHorizonCartUpdate(event) {
    var count = event && event.detail && event.detail.data && event.detail.data.itemCount;
    if (typeof count === 'number') {
      log('cart:update event received, itemCount=' + count);
      updateLegacyBadge(count);
      return;
    }
    // Fallback: fetch /cart.js to get authoritative count
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        log('cart:update event received (no itemCount in detail, fetched /cart.js), count=' + cart.item_count);
        updateLegacyBadge(cart.item_count);
      })
      .catch(function () {});
  }

  function init() {
    log('cart-integration.js init');
    document.addEventListener('click', handleATCSubmit, true);
    document.addEventListener('click', handleCartIconClick, true);

    // Sync legacy badge whenever Horizon dispatches cart:update (both CartAddEvent
    // and CartUpdateEvent use this same event name).
    document.addEventListener('cart:update', handleHorizonCartUpdate);

    // Expose for other callers (QB, utilities, EPA)
    window.cartIntegration = {
      refreshCart: refreshCart,
      openDrawer: openDrawer,
      updateLegacyBadge: updateLegacyBadge,
    };

    // Delay diagnostics slightly so bundle's registrations complete.
    setTimeout(runDiagnostics, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
